/**
 * GA4 tracking for the public agenda views.
 *
 * Two events:
 *   - `add_to_calendar` — fires on click of the "Add to Google Calendar"
 *     anchor inside a section card (calendar / list) or the drawer's
 *     "Add to calendar" link. Deduped per section_id per browser tab.
 *   - `view_section`    — fires when the drawer opens or navigates to a new
 *     section (prev/next/subsection). Not deduped: each surfacing is a
 *     distinct read.
 *
 * Sink detection:
 *   1. `window.gtag`          — direct gtag.js install.
 *   2. `window.dataLayer.push` — GTM install.
 *   3. Neither                — silent no-op.
 */
const SECTION_SELECTOR = "[data-section-id]";
const ICS_SELECTOR = 'a[data-section-gcal="1"]';
const STORAGE_KEY_PREFIX = "hora:add_to_calendar:";
const memoryDedup = new Set();
function wasTracked(sectionId) {
    if (memoryDedup.has(sectionId))
        return true;
    try {
        return sessionStorage.getItem(STORAGE_KEY_PREFIX + sectionId) !== null;
    }
    catch (_a) {
        return false;
    }
}
function markTracked(sectionId) {
    memoryDedup.add(sectionId);
    try {
        sessionStorage.setItem(STORAGE_KEY_PREFIX + sectionId, "1");
    }
    catch (_a) {
        /* storage disabled — memory Set carries the dedup for this tab */
    }
}
function dispatch(eventName, payload) {
    const gtag = window.gtag;
    if (typeof gtag === "function") {
        try {
            gtag("event", eventName, payload);
            return true;
        }
        catch (_a) {
            /* fall through to dataLayer */
        }
    }
    const dl = window.dataLayer;
    if (Array.isArray(dl)) {
        try {
            dl.push(Object.assign({ event: eventName }, payload));
            return true;
        }
        catch (_b) {
            /* silent — no sink accepted */
        }
    }
    return false;
}
function buildPayload(card, view) {
    var _a, _b, _c;
    const rawId = card.getAttribute("data-section-id");
    const sectionId = rawId ? Number(rawId) : NaN;
    if (!Number.isFinite(sectionId))
        return null;
    const panelIdRaw = card.getAttribute("data-panel-id");
    const panelIdNum = panelIdRaw ? Number(panelIdRaw) : NaN;
    return {
        event_category: "Agenda",
        section_id: sectionId,
        section_title: ((_a = card.getAttribute("data-section-title")) !== null && _a !== void 0 ? _a : "").trim(),
        section_type: ((_b = card.getAttribute("data-section-type")) !== null && _b !== void 0 ? _b : "").trim(),
        panel_id: Number.isFinite(panelIdNum) ? panelIdNum : null,
        panel_title: ((_c = card.getAttribute("data-panel-title")) !== null && _c !== void 0 ? _c : "").trim(),
        view,
    };
}
function onClick(view, evt) {
    const target = evt.target;
    if (!target)
        return;
    const ics = target.closest(ICS_SELECTOR);
    if (!ics)
        return;
    const card = ics.closest(SECTION_SELECTOR);
    if (!card)
        return;
    const payload = buildPayload(card, view);
    if (!payload)
        return;
    if (wasTracked(payload.section_id))
        return;
    if (dispatch("add_to_calendar", payload)) {
        markTracked(payload.section_id);
    }
}
export function trackSectionView(opts) {
    if (!Number.isFinite(opts.sectionId))
        return;
    const payload = {
        event_category: "Agenda",
        section_id: opts.sectionId,
        section_title: opts.sectionTitle.trim(),
        section_type: opts.sectionType.trim(),
        panel_id: opts.panelId,
        panel_title: opts.panelTitle.trim(),
        agenda_id: opts.agendaId,
        view: opts.view,
        source: opts.source,
    };
    dispatch("view_section", payload);
}
export function initAddToCalendarTracking(container, view) {
    container.addEventListener("click", (e) => onClick(view, e));
}
